import { takeEvery, take, put, fork, select, race } from 'redux-saga/effects';
import omit from 'lodash/omit';
import {
  loggedUserSelector,
  targetUserPermissionsSelector,
  isLoggedUserSelector,
  targetUserSelector,
} from '../context/selectors';
import {
  CIRCLE_CAREGIVER,
  CIRCLE_DEPENDENTS,
  CIRCLE_FAMILY,
} from '../../constants/strings';

// circles
import { consts as myFamilyCircleConsts } from './myFamilyCircle';
import { consts as leaveFamilyCircleConsts } from './leaveFamilyCircle';
import { consts as removeCaregiverConsts } from './removeCaregiver';
import { consts as declineFamilyInviteConsts } from './declineInviteToFamilyCircle';
import { consts as acceptFamilyInviteConsts } from './acceptInviteToFamilyCircle';
import { consts as memberFamilyCircleConsts } from './memberFamilyCircle';
import { consts as declineCaregiverRoleConsts } from './declineCaregiverRole';
import { consts as acceptCaregiverRoleConsts } from './acceptCaregiverRole';
import { consts as acceptGuardianRoleConsts } from './accept-guardian-role';
import { consts as declineGuardianRoleConsts } from './decline-guardian-role';
import { consts as membersCaregiverCircleConsts } from './caregiver';
import { consts as membersGuardianCircleConsts } from './get-guardian-circle';
import { consts as removeGuardianRoleConsts } from './remove-guardian-role';
import { consts as getGuardianDependentsConsts } from '../profiles/get-guardian-dependents';
// pending invitations
import { consts as cancelFamilyMemberInvitationConsts } from './cancelFamilyMemberInvitation';
import { consts as cancelCaregiverInvitationConsts } from './cancelCaregiverInvitation';
import { consts as inviteToFamilyCircleConsts } from './inviteToFamilyCircle';
import { consts as inviteCaregiverConsts } from './inviteCaregiver';
// invitations
import { consts as familyMemberInviteConsts } from '../invitations/familyMember';
import { consts as caregiverInvitesConsts } from '../invitations/caregiver';
import { consts as guardianInvitesConsts } from '../invitations/guardian';
// context
import { consts as targetUserConsts } from '../context/targetUser';
// form in progress
import { consts as formInProgressConsts } from '../ui/formInProgress';
import { actions as scheduledTasksActions } from '../healthboard/requests/scheduled-tasks-upcoming-req';
import * as error from '../ui/error';

/** *************************** Family circle ********************************** * */

function* acceptCaregiverRole(action) {
  const { id } = action.payload;
  const targetUser = yield select(targetUserSelector);

  try {
    yield take(acceptCaregiverRoleConsts.SUCCEED);
    yield put({
      type: caregiverInvitesConsts.REMOVE,
      payload: { id },
    });
    yield put({
      type: memberFamilyCircleConsts.REMOVE_ITEM,
      payload: { id: targetUser.id },
    });
    yield put({
      type: membersCaregiverCircleConsts.SEND,
    });

    if (targetUser) {
      const payload = {
        ...omit(targetUser, ['inviteId', 'inviteType']),
        activeCircle: CIRCLE_CAREGIVER,
      };
      yield put({
        type: targetUserConsts.SET,
        payload,
      });
    }
  } catch (err) {
    console.error('Error on accept caregiver invite', err);
  }
}

function* declineCaregiverRole(action) {
  const { id } = action.payload;
  const loggedUser = yield select(loggedUserSelector);

  try {
    yield take(declineCaregiverRoleConsts.SUCCEED);
    yield put({
      type: caregiverInvitesConsts.REMOVE,
      payload: { id },
    });
    yield put({
      type: targetUserConsts.SET,
      payload: loggedUser,
    });
  } catch (err) {
    console.error('Error decline caregiver role', id, err);
  }
}

function* patientRemoveCaregiverRole(id) {
  try {
    yield put({
      type: myFamilyCircleConsts.REMOVE_CAREGIVER_ROLE,
      payload: { id },
    });
  } catch (err) {
    console.error('Error removing caregiver role', id, err);
  }
}

function* rejectedAcceptOrDecline() {
  const targetUser = yield select(targetUserSelector);

  yield put({
    type: error.consts.UPDATE,
    payload: { id: 'errors.familyMember.invitation' },
  });

  yield put(
    scheduledTasksActions.send({ id: targetUser.id, limit: 20, offset: 0 }),
  );
}

function* acceptFamilyInvite(action) {
  const { id } = action.payload;
  const targetUser = yield select(targetUserSelector);
  const { success } = yield race({
    success: take(acceptFamilyInviteConsts.SUCCEED),
    reject: take(acceptFamilyInviteConsts.REJECTED),
  });

  if (success) {
    yield put({
      type: familyMemberInviteConsts.REMOVE,
      payload: { id },
    });
    yield put({
      type: memberFamilyCircleConsts.SEND,
    });

    if (targetUser) {
      const payload = {
        ...omit(targetUser, ['inviteId', 'inviteType']),
        activeCircle: CIRCLE_FAMILY,
      };

      yield put({
        type: targetUserConsts.SET,
        payload,
      });
    }
  } else {
    yield fork(rejectedAcceptOrDecline);
  }
}

function* declineFamilyInvite(action) {
  const { id } = action.payload;
  const loggedUser = yield select(loggedUserSelector);

  const { success } = yield race({
    success: take(declineFamilyInviteConsts.SUCCEED),
    reject: take(declineFamilyInviteConsts.REJECTED),
  });

  if (success) {
    yield put({
      type: familyMemberInviteConsts.REMOVE,
      payload: { id },
    });
    yield put({
      type: targetUserConsts.SET,
      payload: loggedUser,
    });
  } else {
    yield fork(rejectedAcceptOrDecline);
  }
}

/** *************************** Guardian circle ************************************* * */

function* guardianRemovesGuardianRole(action) {
  const { userId, memberId } = action.payload;
  const loggedUser = yield select(loggedUserSelector);
  const { id } = loggedUser;
  yield take(removeGuardianRoleConsts.SUCCEED);
  if (id === memberId) {
    yield put({
      type: membersGuardianCircleConsts.REMOVE_ITEM,
      payload: { id: userId },
    });
    yield put({
      type: membersCaregiverCircleConsts.SEND,
    });
    yield put({
      type: memberFamilyCircleConsts.SEND,
    });
    yield put({
      type: getGuardianDependentsConsts.SEND,
    });
    yield put({
      type: targetUserConsts.SET,
      payload: loggedUser,
    });
  } else {
    yield put({
      type: membersGuardianCircleConsts.REMOVE_GUARDIAN_ROLE,
      payload: { userId, memberId },
    });
  }
}

function* acceptGuardianRole(action) {
  const { id } = action.payload;
  const targetUser = yield select(targetUserSelector);

  yield take(acceptGuardianRoleConsts.SUCCEED);
  yield put({
    type: guardianInvitesConsts.REMOVE,
    payload: { id },
  });
  yield put({
    type: memberFamilyCircleConsts.REMOVE_ITEM,
    payload: { id: targetUser.id },
  });
  yield put({
    type: membersCaregiverCircleConsts.REMOVE_ITEM,
    payload: { id },
  });
  yield put({
    type: membersGuardianCircleConsts.SEND,
  });
  yield put({
    type: getGuardianDependentsConsts.SEND,
  });

  if (targetUser) {
    const payload = {
      ...omit(targetUser, ['inviteId', 'inviteType']),
      activeCircle: CIRCLE_DEPENDENTS,
    };
    yield put({
      type: targetUserConsts.SET,
      payload,
    });
  }
}

function* declineGuardianRole(action) {
  const { id } = action.payload;
  const loggedUser = yield select(loggedUserSelector);
  yield take(declineGuardianRoleConsts.SUCCEED);
  yield put({
    type: guardianInvitesConsts.REMOVE,
    payload: { id },
  });
  yield put({
    type: targetUserConsts.SET,
    payload: loggedUser,
  });
}

/** *************************** Caregiver circle ********************************** * */

function* caregiverRemoveCaregiverRole(userId, memberId) {
  try {
    yield put({
      type: membersCaregiverCircleConsts.REMOVE_CAREGIVER,
      payload: { userId, memberId },
    });
    yield put({
      type: membersGuardianCircleConsts.REMOVE_CAREGIVER,
      payload: { userId, memberId },
    });
  } catch (err) {
    console.error('Error removeing caregiver role', err);
  }
}

function* leaveCaregiverRole(targetUser) {
  try {
    yield put({
      type: membersCaregiverCircleConsts.SEND,
    });
    yield put({
      type: memberFamilyCircleConsts.SEND,
    });

    if (targetUser) {
      yield put({
        type: targetUserConsts.SET,
        payload: { ...targetUser, activeCircle: CIRCLE_FAMILY },
      });
    }
  } catch (err) {
    console.error('Error on remove caregiver from caregiver circle', err);
  }
}

function* removeCaregiverRole(action) {
  const { userId, memberId } = action.payload;
  const state = yield select();
  const {
    isLoggedUser,
    isCaregiver,
    isVerifiedDependent,
  } = targetUserPermissionsSelector(state);
  const loggedUser = loggedUserSelector(state);
  const targetUser = targetUserSelector(state);
  const isCaregiverLeaveRole =
    isCaregiver && !isVerifiedDependent && loggedUser.id === memberId;
  const isCaregiverRemoveRole =
    (isCaregiver && loggedUser.id !== memberId) || isVerifiedDependent;

  yield take(removeCaregiverConsts.SUCCEED);

  if (isLoggedUser) {
    yield fork(patientRemoveCaregiverRole, memberId);
  } else if (isCaregiverLeaveRole) {
    yield fork(leaveCaregiverRole, targetUser);
  } else if (isCaregiverRemoveRole) {
    yield fork(caregiverRemoveCaregiverRole, userId, memberId);
  }
}

/** ************************************************************************** * */

/** ***************************** WATCHERS *********************************** * */
/** ************************************************************************** * */

function* watchAcceptCaregiverRole() {
  yield takeEvery(acceptCaregiverRoleConsts.SEND, acceptCaregiverRole);
}

function* watchDeclineCaregiverRole() {
  yield takeEvery(declineCaregiverRoleConsts.SEND, declineCaregiverRole);
}

function* watchRemoveCaregiverRole() {
  yield takeEvery(removeCaregiverConsts.SEND, removeCaregiverRole);
}

function* watchAccceptFamilyInvite() {
  yield takeEvery(acceptFamilyInviteConsts.SEND, acceptFamilyInvite);
}

function* watchDeclineFamilyInvite() {
  yield takeEvery(declineFamilyInviteConsts.SEND, declineFamilyInvite);
}

function* watchRemoveAnotherGuardian() {
  yield takeEvery(removeGuardianRoleConsts.SEND, guardianRemovesGuardianRole);
}

function* watchAcceptGuardianRole() {
  yield takeEvery(acceptGuardianRoleConsts.SEND, acceptGuardianRole);
}

function* watchDeclineGuardianRole() {
  yield takeEvery(declineGuardianRoleConsts.SEND, declineGuardianRole);
}

/** *************************** Pending invitations ************************** * */

/*
 * There's strange bug now with mocked invitationId data:
 * when user invites someone to be a familyMember/caregiver
 * and immediately cancels invitation - an error occured
 * (as there's no such invitationId in the system), and after that
 * when user cancels another invitation (valid one) -
 * errored cancelation behaves on UI as it's cancelled successfully
 * (but it's not as inviteId was incorrect), and after page refresh
 * errored cancelation apper on UI as not canceled. ^_^
 */

/** ***************************** My family Circle *************************** * */

/* Add new pending invitation */

function* addPendingWorker(action) {
  try {
    const succeedAction = yield take(inviteToFamilyCircleConsts.SUCCEED);
    if (!action.payload.isCaregiver) {
      yield put({
        type: myFamilyCircleConsts.ADD_INVITATION,
        payload: succeedAction.payload,
      });
    }
  } catch (err) {
    console.error('Error add family member invitation', err);
  }
}

function* addPendingSaga() {
  yield takeEvery(inviteToFamilyCircleConsts.SEND, addPendingWorker);
}

/* Clear requests whether we clear form in progress */
function* clearFormInProgressWorker() {
  yield put({ type: inviteToFamilyCircleConsts.CLEAR });
}

function* clearFormInProgressSaga() {
  yield takeEvery(formInProgressConsts.DELETE, clearFormInProgressWorker);
}

/* Remove pending invitation */

function* removePendingWorker(action) {
  try {
    yield take(cancelFamilyMemberInvitationConsts.SUCCEED);
    yield put({
      type: myFamilyCircleConsts.REMOVE_INVITATION,
      payload: action.payload,
    });
  } catch (err) {
    console.error('Error remove family member invitation', err);
  }
}

function* removePendingSaga() {
  yield takeEvery(cancelFamilyMemberInvitationConsts.SEND, removePendingWorker);
}

/* Add new pending caregiver invitation */

function* addPendingCaregiverWorker(action) {
  try {
    const successAction = yield take(inviteCaregiverConsts.SUCCEED);
    yield put({
      type: myFamilyCircleConsts.ADD_CAREGIVER_INVITATION,
      payload: successAction.payload,
    });
  } catch (err) {
    console.error('Error add caregiver invitation', err);
  }
}

function* addPendingCaregiverSaga() {
  yield takeEvery(inviteCaregiverConsts.SEND, addPendingCaregiverWorker);
}

/* Remove caregiver invitation */

function* removePendingCaregiverWorker(action) {
  try {
    yield take(cancelCaregiverInvitationConsts.SUCCEED);
    yield put({
      type: myFamilyCircleConsts.REMOVE_CAREGIVER_INVITATION,
      payload: action.payload,
    });
  } catch (err) {
    console.error('Error remove caregiver invitation', err);
  }
}

function* removePendingCaregiverSaga() {
  yield takeEvery(
    cancelCaregiverInvitationConsts.SEND,
    removePendingCaregiverWorker,
  );
}

/** ***************************** Caregiver Circle *************************** * */

/* Add new pending invitation */

function* caregiverAddPendingWorker(action) {
  try {
    const succeedAction = yield take(inviteToFamilyCircleConsts.SUCCEED);

    if (action.payload.isCaregiver) {
      yield put({
        type: membersCaregiverCircleConsts.ADD_INVITATION,
        payload: Object.assign(action.payload, succeedAction.payload),
      });
    } else {
      yield put({
        type: membersGuardianCircleConsts.ADD_INVITATION,
        payload: Object.assign(action.payload, succeedAction.payload),
      });
    }
  } catch (err) {
    console.error('Error add family member invitation', err);
  }
}

function* caregiverAddPendingSaga() {
  yield takeEvery(inviteToFamilyCircleConsts.SEND, caregiverAddPendingWorker);
}

// /* Remove pending invitation */

function* caregiverRemovePendingWorker(action) {
  try {
    yield take(cancelFamilyMemberInvitationConsts.SUCCEED);
    yield put({
      type: membersCaregiverCircleConsts.REMOVE_INVITATION,
      payload: action.payload,
    });
    yield put({
      type: membersGuardianCircleConsts.REMOVE_INVITATION,
      payload: action.payload,
    });
  } catch (err) {
    console.error('Error remove family member invitation', err);
  }
}

function* caregiverRemovePendingSaga() {
  yield takeEvery(
    cancelFamilyMemberInvitationConsts.SEND,
    caregiverRemovePendingWorker,
  );
}

export default function* root() {
  yield fork(watchAcceptCaregiverRole);
  yield fork(watchDeclineCaregiverRole);
  yield fork(watchAccceptFamilyInvite);
  yield fork(watchRemoveCaregiverRole);
  yield fork(watchDeclineFamilyInvite);
  yield fork(addPendingSaga);
  yield fork(removePendingSaga);
  yield fork(addPendingCaregiverSaga);
  yield fork(removePendingCaregiverSaga);
  yield fork(caregiverAddPendingSaga);
  yield fork(caregiverRemovePendingSaga);
  yield fork(clearFormInProgressSaga);
  yield fork(watchRemoveAnotherGuardian);
  yield fork(watchAcceptGuardianRole);
  yield fork(watchDeclineGuardianRole);
}



// WEBPACK FOOTER //
// ./src/redux/circles/circlesSaga.js